<script setup>
import { computed, h } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccountStatusIndicator } from 'dashboard/composables/useAccountStatusIndicator';
import { usePolicy } from 'dashboard/composables/usePolicy';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import AgentCell from './AgentCell.vue';
import {
  formatFirstLoginTime,
  sortByKey,
} from '../../helpers/firstLoginHelper';

const { agents, agentMetrics, agentFirstLogins } = defineProps({
  agents: {
    type: Array,
    default: () => [],
  },
  agentMetrics: {
    type: Array,
    default: () => [],
  },
  // { agentId: unix seconds } of today's first login, keyed by agent id
  agentFirstLogins: {
    type: Object,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const accountStatuses = useMapGetter('accountStatuses/getStatuses');
const { indicatorFor } = useAccountStatusIndicator();
const { uiSettings, updateUISettings } = useUISettings();
const { checkPermissions } = usePolicy();

const canViewAgentReport = computed(() =>
  checkPermissions(['reports.agents.view'])
);

// UI Settings key for agent table page size
const AGENT_TABLE_PAGE_SIZE_KEY = 'report_overview_agent_table_page_size';

// Get the saved page size from UI settings or default to 10
const getPageSize = () => {
  return uiSettings.value[AGENT_TABLE_PAGE_SIZE_KEY] || 10;
};

const handlePageSizeChange = pageSize => {
  updateUISettings({ [AGENT_TABLE_PAGE_SIZE_KEY]: pageSize });
};

const getAgentMetrics = id =>
  agentMetrics.find(metrics => metrics.assignee_id === Number(id)) || {};

// Internal accounts (administrator, support, implementer) are not part of the floor headcount.
const REPORTED_ROLES = ['agent', 'supervisor'];

const isOnline = status =>
  (accountStatuses.value.find(item => item.key === status)?.base_availability ||
    status) === 'online';

const tableData = computed(() =>
  agents
    .filter(agent => REPORTED_ROLES.includes(agent.role))
    .map(agent => {
      const metric = getAgentMetrics(agent.id);
      return {
        id: agent.id,
        agent: agent.available_name || agent.name,
        email: agent.email,
        thumbnail: agent.thumbnail,
        firstLoginAt: agentFirstLogins[agent.id],
        open: metric.open || 0,
        unattended: metric.unattended || 0,
        snoozed: metric.snoozed || 0,
        status: agent.availability_status,
      };
    })
    .sort(
      (a, b) =>
        isOnline(b.status) - isOnline(a.status) ||
        sortByKey('firstLoginAt', 'agent')(a, b)
    )
);

const defaulSpanRender = cellProps =>
  h(
    'span',

    {
      class: cellProps.getValue()
        ? 'capitalize text-n-slate-12'
        : 'capitalize text-n-slate-11',
    },
    cellProps.getValue() ? cellProps.getValue() : '---'
  );

const STATUS_DOT = {
  online: 'bg-n-teal-9',
  busy: 'bg-n-amber-9',
  offline: 'bg-n-slate-8',
};

const statusLabel = status => {
  if (!status) return '---';
  const customStatus = accountStatuses.value.find(
    item => item.key === status && !item.builtin
  );
  return customStatus
    ? customStatus.description
    : t(`OVERVIEW_REPORTS.AGENT_STATUS.${status.toUpperCase()}`);
};

const renderStatus = cellProps => {
  const status = cellProps.getValue();
  const indicator = indicatorFor(status, STATUS_DOT) ?? {
    class: 'bg-n-slate-8',
  };
  return h(
    'span',
    { class: 'flex items-center gap-2 capitalize text-n-slate-12' },
    [
      h('span', {
        class: ['w-2 h-2 rounded-full', indicator.class],
        style: indicator.style,
      }),
      statusLabel(status),
    ]
  );
};

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('agent', {
    header: () => t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.AGENT'),
    cell: cellProps =>
      h(AgentCell, { ...cellProps, isClickable: canViewAgentReport.value }),
    size: 250,
  }),
  columnHelper.accessor('status', {
    header: () => t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.STATUS'),
    cell: renderStatus,
    size: 130,
  }),
  columnHelper.accessor('firstLoginAt', {
    header: () =>
      t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.FIRST_LOGIN'),
    cell: cellProps =>
      h(
        'span',
        { class: 'text-n-slate-12' },
        formatFirstLoginTime(cellProps.getValue())
      ),
    size: 110,
  }),
  columnHelper.accessor('open', {
    header: () => t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.OPEN'),
    cell: defaulSpanRender,
    size: 100,
  }),
  columnHelper.accessor('unattended', {
    header: () =>
      t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.UNATTENDED'),
    cell: defaulSpanRender,
    size: 100,
  }),
  columnHelper.accessor('snoozed', {
    header: () =>
      t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.SNOOZED'),
    cell: defaulSpanRender,
    size: 100,
  }),
];

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    pagination: {
      pageSize: getPageSize(),
    },
  },
});
</script>

<template>
  <div class="flex flex-col flex-1">
    <Table :table="table" class="max-h-[calc(100vh-21.875rem)]" />
    <Pagination
      class="mt-2"
      :table="table"
      show-page-size-selector
      :default-page-size="getPageSize()"
      @page-size-change="handlePageSizeChange"
    />
    <div
      v-if="isLoading"
      class="items-center flex text-base justify-center p-8"
    >
      <Spinner />
      <span>
        {{ $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.LOADING_MESSAGE') }}
      </span>
    </div>
    <EmptyState
      v-else-if="!isLoading && !agents.length"
      :title="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.NO_AGENTS')"
    />
  </div>
</template>
